import { getLocale, setLocale, t, type Locale } from '../lib/i18n';
import { useUpdateProfile } from '../lib/team';
import { cn } from '../lib/cn';

/**
 * Selector de idioma ES|PT para la app autenticada (topbar). A diferencia del
 * de las pantallas públicas, éste PERSISTE la elección en el perfil del usuario
 * (`PATCH /me`) para que se mantenga entre sesiones.
 *
 * `t()` lee un locale global no reactivo: `setLocale()` notifica a los
 * suscriptores y App re-renderiza todo el árbol (ver `useLocaleSync` en App.tsx),
 * así el cambio de idioma se aplica en caliente mientras el usuario trabaja.
 */
export function LanguageSwitch({ className }: { className?: string }) {
  const current = getLocale();
  const update = useUpdateProfile();

  const switchTo = (next: Locale) => {
    if (next === current) return;
    setLocale(next, true); // elección manual → dispara re-render global + persiste
    document.documentElement.lang = next === 'pt' ? 'pt-BR' : 'es';
    update.mutate({ locale: next }); // persistir en el perfil
  };

  return (
    <div
      role="group"
      aria-label={t('profile.locale')}
      className={cn(
        'inline-flex overflow-hidden rounded-token border border-line font-mono text-xs',
        className,
      )}
    >
      {(['es', 'pt'] as const).map((l) => (
        <button
          key={l}
          type="button"
          aria-pressed={current === l}
          onClick={() => switchTo(l)}
          className={
            current === l
              ? 'bg-brand px-2.5 py-1.5 text-brand-ink'
              : 'px-2.5 py-1.5 text-muted transition-colors hover:text-ink'
          }
        >
          {l.toUpperCase()}
        </button>
      ))}
    </div>
  );
}
